import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import {
  Camera, Mail, Phone, Facebook, Instagram, Twitter, Linkedin, Youtube, Check, Link2,
  User, FileText, Share2, Rss, Bell,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { key: "facebook", label: "Facebook", icon: Facebook, prefix: "", placeholder: "facebook.com/yourpage" },
  { key: "instagram", label: "Instagram", icon: Instagram, prefix: "@", placeholder: "yourhandle" },
  { key: "twitter", label: "X (Twitter)", icon: Twitter, prefix: "@", placeholder: "yourhandle" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, prefix: "", placeholder: "linkedin.com/in/you" },
  { key: "youtube", label: "YouTube", icon: Youtube, prefix: "@", placeholder: "yourchannel" },
];

const NAV = [
  { key: "profile", label: "Profile", icon: User },
  { key: "contact", label: "Contact", icon: Phone },
  { key: "about", label: "About You", icon: FileText },
  { key: "social", label: "Social Media", icon: Share2 },
  { key: "feeds", label: "Connected Feeds", icon: Rss },
  { key: "preferences", label: "Preferences", icon: Bell },
];

export default function Account() {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [section, setSection] = useState("profile");
  const [form, setForm] = useState({ name: "", phone: "", location: "", focus: "", bio: "", daily_email: true, socials: {}, connected_platforms: [] });
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", phone: user.phone || "", location: user.location || "",
      focus: user.focus || "", bio: user.bio || "", daily_email: user.daily_email !== false,
      socials: user.socials || {}, connected_platforms: user.connected_platforms || [],
    });
  }, [user]);

  const setSocial = (key, val) => setForm((f) => ({ ...f, socials: { ...f.socials, [key]: val } }));
  const toggleConnect = (key) => setForm((f) => {
    const has = f.connected_platforms.includes(key);
    return { ...f, connected_platforms: has ? f.connected_platforms.filter((k) => k !== key) : [...f.connected_platforms, key] };
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Your account has been updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Photo updated.");
    } catch { toast.error("Upload failed"); } finally { setAvatarBusy(false); }
  };

  return (
    <div className="min-h-screen bg-bone pt-24 pb-24">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Your Account</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">Settings</h1>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-charcoal/10 border border-charcoal/10 mt-12">
          {/* Left-side navigation */}
          <aside className="md:col-span-4 lg:col-span-3 bg-bone" data-testid="settings-nav">
            <nav className="flex md:flex-col overflow-x-auto">
              {NAV.map((n) => (
                <button key={n.key} onClick={() => setSection(n.key)} data-testid={`settings-nav-${n.key}`}
                  className={`flex items-center gap-3 px-6 py-4 md:py-5 border-b border-charcoal/10 whitespace-nowrap transition-colors text-left ${section === n.key ? "bg-navy text-bone" : "hover:bg-sand text-charcoal"}`}>
                  <n.icon size={17} strokeWidth={1.6} />
                  <span className="font-mono text-xs uppercase tracking-widest">{n.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Right panel */}
          <section className="md:col-span-8 lg:col-span-9 bg-bone">
            <form onSubmit={save} data-testid="account-form">
              <div className="p-8 md:p-12 min-h-[420px]">
                {section === "profile" && (
                  <div>
                    <SectionHead title="Profile" desc="Your photo and name across the community." />
                    <div className="flex items-center gap-8 mt-8">
                      <button type="button" onClick={() => fileRef.current?.click()} data-testid="account-avatar-btn"
                        className="relative group/av w-40 h-40 shrink-0 overflow-hidden border border-charcoal/10">
                        {user?.avatar ? <img src={user.avatar} alt="" className="w-40 h-40 object-cover" />
                          : <div className="w-40 h-40 bg-navy text-bone flex items-center justify-center font-serif text-6xl">{user?.name?.[0]}</div>}
                        <span className="absolute inset-0 bg-charcoal/60 opacity-0 group-hover/av:opacity-100 transition-opacity flex items-center justify-center"><Camera size={22} className="text-bone" strokeWidth={1.5} /></span>
                        {avatarBusy && <span className="absolute inset-0 bg-charcoal/70 flex items-center justify-center"><span className="w-6 h-6 border-2 border-bone border-t-transparent rounded-full animate-spin" /></span>}
                      </button>
                      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} data-testid="account-avatar-input" />
                      <div>
                        <h4 className="font-serif text-xl tracking-tight">Profile Photo</h4>
                        <p className="font-sans text-sm text-charcoal/60 mt-1 max-w-xs">Click your photo to upload a new one (JPG, PNG, or WEBP).</p>
                      </div>
                    </div>
                    <div className="mt-10 max-w-md">
                      <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="account-name" />
                    </div>
                  </div>
                )}

                {section === "contact" && (
                  <div>
                    <SectionHead title="Contact" desc="How brothers and the ministry can reach you." />
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl">
                      <div>
                        <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60 flex items-center gap-2"><Mail size={12} /> Email</label>
                        <input value={user?.email || ""} readOnly data-testid="account-email"
                          className="w-full bg-transparent border-b border-charcoal/20 py-3 font-sans text-charcoal/50 cursor-not-allowed" />
                      </div>
                      <div>
                        <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60 flex items-center gap-2"><Phone size={12} /> Telephone</label>
                        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: formatUSPhone(e.target.value) })}
                          placeholder="(555) 123-4567" inputMode="tel" data-testid="account-phone"
                          className="w-full bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-navy transition-colors" />
                      </div>
                      <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="account-location" placeholder="City, State" />
                    </div>
                  </div>
                )}

                {section === "about" && (
                  <div>
                    <SectionHead title="About You" desc="Share your focus and story with the brotherhood." />
                    <div className="mt-8 max-w-2xl space-y-6">
                      <Field label="Focus" value={form.focus} onChange={(v) => setForm({ ...form, focus: v })} testid="account-focus" placeholder="e.g. Leadership & Wellness" />
                      <div>
                        <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Bio</label>
                        <textarea value={form.bio} rows={4} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="account-bio"
                          className="w-full bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-navy transition-colors resize-none" />
                      </div>
                    </div>
                  </div>
                )}

                {section === "social" && (
                  <div>
                    <SectionHead title="Social Media" desc="Add your handles so brothers can follow and connect with you." />
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl">
                      {PLATFORMS.map((p) => (
                        <div key={p.key}>
                          <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60 flex items-center gap-2"><p.icon size={13} /> {p.label}</label>
                          <div className="flex items-center border-b border-charcoal/30 focus-within:border-navy transition-colors">
                            {p.prefix && <span className="font-sans text-charcoal/40 pr-1">{p.prefix}</span>}
                            <input value={form.socials[p.key] || ""} onChange={(e) => setSocial(p.key, e.target.value)} placeholder={p.placeholder} data-testid={`social-${p.key}`}
                              className="w-full bg-transparent py-3 font-sans focus:outline-none" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {section === "feeds" && (
                  <div>
                    <SectionHead title="Connected Feeds" desc="Connect a feed so your community posts can be shared there." />
                    <div className="mt-8 space-y-px bg-charcoal/10 border border-charcoal/10 max-w-2xl">
                      {PLATFORMS.map((p) => {
                        const handle = form.socials[p.key];
                        const connected = form.connected_platforms.includes(p.key);
                        return (
                          <div key={p.key} className="bg-bone flex items-center justify-between gap-4 p-4" data-testid={`feed-${p.key}`}>
                            <div className="flex items-center gap-3">
                              <span className="w-9 h-9 bg-navy text-bone flex items-center justify-center"><p.icon size={16} strokeWidth={1.5} /></span>
                              <div>
                                <span className="font-sans text-sm block">{p.label}</span>
                                <span className="font-mono text-[10px] text-charcoal/40">{handle ? `${p.prefix}${handle}` : "No handle added"}</span>
                              </div>
                            </div>
                            <button type="button" disabled={!handle} onClick={() => toggleConnect(p.key)} data-testid={`connect-${p.key}`}
                              className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors disabled:opacity-40 ${connected ? "bg-navy text-bone" : "border border-charcoal/30 hover:bg-charcoal hover:text-bone"}`}>
                              {connected ? <><Check size={13} /> Connected</> : <><Link2 size={13} /> Connect</>}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <p className="font-sans text-xs text-charcoal/40 mt-4 max-w-2xl">Handles and connections are saved to your profile now. Live cross-posting to external platforms activates once platform access is provided.</p>
                  </div>
                )}

                {section === "preferences" && (
                  <div>
                    <SectionHead title="Preferences" desc="Control what lands in your inbox." />
                    <label className="mt-8 flex items-center justify-between gap-3 cursor-pointer border border-charcoal/10 p-6 max-w-md" data-testid="account-daily-toggle">
                      <span className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/70">Receive the daily devotional email</span>
                      <input type="checkbox" checked={form.daily_email} onChange={(e) => setForm({ ...form, daily_email: e.target.checked })} className="accent-navy w-4 h-4" />
                    </label>
                  </div>
                )}
              </div>

              <div className="p-8 md:px-12 border-t border-charcoal/10 bg-sand/40">
                <button type="submit" disabled={saving} data-testid="account-save"
                  className="bg-charcoal text-bone px-10 py-4 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

function formatUSPhone(value) {
  const d = (value || "").replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function SectionHead({ title, desc }) {
  return (
    <div>
      <h3 className="font-serif text-3xl tracking-tight">{title}</h3>
      <p className="font-sans text-sm text-charcoal/60 mt-2">{desc}</p>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-navy transition-colors" />
    </div>
  );
}
